import React, { useEffect, useState, useContext } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { AppContext } from '../../../Context/AppContext';
import styles from './StudentHomework.module.css';

const StudentHomework = () => {
    const { user } = useContext(AppContext);
    const [homeworks, setHomeworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'deadline', direction: 'asc' });
    const [isCR, setIsCR] = useState(false);

    // ✅ Modal state
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [currentEdit, setCurrentEdit] = useState(null);

    // ✅ Form state
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        deadline: '',
        submissionLink: '',
    });

    const departmentMap = {
        CS: 'Computer Science',
        EE: 'Electrical Engineering',
        ME: 'Mechanical Engineering',
    };

    // ✅ Check if user is CR
    const checkIfCR = async () => {
        try {
            const crRef = collection(db, "CR");
            const q = query(crRef, where("rollNumber", "==", user.stdRegNumber.trim()));
            const crSnap = await getDocs(q);
            if (!crSnap.empty) setIsCR(true);
        } catch (err) {
            console.error("Error checking CR:", err);
        }
    };

    useEffect(() => {
        if (!user) return;

        const fetchHomeworks = async () => {
            setLoading(true);
            setError('');

            try {
                const q = query(collection(db, 'homeworks'));
                const querySnapshot = await getDocs(q);

                let data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                data = data.filter(hw => {
                    const mappedDept = user.department
                        ? departmentMap[user.department.trim()] || user.department.trim()
                        : null;

                    return (
                        (!user.program || hw.program?.trim() === user.program.trim()) &&
                        (!mappedDept || hw.department?.trim() === mappedDept) &&
                        (!user.semester || hw.semester === Number(user.semester)) &&
                        (!user.section || hw.section?.trim() === user.section.trim())
                    );
                });

                setHomeworks(data);
            } catch (err) {
                console.error('Error fetching homeworks:', err);
                setError('Failed to load homeworks. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchHomeworks();
        checkIfCR();
    }, [user]);

    // ✅ Delete Homework
    const handleDelete = async (id) => {
        try {
            await deleteDoc(doc(db, "homeworks", id));
            setHomeworks(prev => prev.filter(hw => hw.id !== id));
        } catch (err) {
            console.error("Error deleting homework:", err);
        }
    };

    // ✅ Open Edit Form
    const handleEdit = (hw) => {
        setCurrentEdit(hw);
        setFormData({
            title: hw.title,
            description: hw.description,
            deadline: hw.deadline,
            submissionLink: hw.submissionLink || '',
        });
        setShowEditForm(true);
    };

    // ✅ Update Homework
    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const hwRef = doc(db, "homeworks", currentEdit.id);
            await updateDoc(hwRef, { ...formData });
            setHomeworks(prev =>
                prev.map(hw => (hw.id === currentEdit.id ? { ...hw, ...formData } : hw))
            );
            setShowEditForm(false);
        } catch (err) {
            console.error("Error updating homework:", err);
        }
    };

    // ✅ Add Homework
    const handleAddHomework = async (e) => {
        e.preventDefault();
        try {
            const hwRef = await addDoc(collection(db, "homeworks"), {
                ...formData,
                program: user.program,
                department: departmentMap[user.department.trim()] || user.department.trim(),
                semester: Number(user.semester),
                section: user.section,
                createdAt: new Date().toISOString(),
            });

            setHomeworks(prev => [...prev, { id: hwRef.id, ...formData }]);
            setFormData({ title: '', description: '', deadline: '', submissionLink: '' });
            setShowAddForm(false);
        } catch (err) {
            console.error("Error adding homework:", err);
        }
    };

    const sortedHomeworks = [...homeworks].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'deadline') {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
        } else {
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const formatDeadline = (dateString) => {
        const date = new Date(dateString);
        return `${date.getDate()}-${date.toLocaleString('en-US', { month: 'long' })}-${date.getFullYear()}`;
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    if (loading) return <p className={styles.message}>Loading homeworks...</p>;
    if (error) return <p className={styles.messageError}>{error}</p>;

    return (
        <div className={styles.container}>
            <h2 className={styles.heading}>Your Homeworks</h2>

            {/* ✅ Add Homework Button */}
            {isCR && !showAddForm && (
                <button className={styles.addBtn} onClick={() => setShowAddForm(true)}>
                    ➕ Add Homework
                </button>
            )}

            {/* ✅ Add Homework Form */}
            {showAddForm && (
                <form onSubmit={handleAddHomework} className={styles.modalForm}>
                    <h3>Add Homework</h3>
                    <input
                        type="text"
                        placeholder="Title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                    />
                    <textarea
                        placeholder="Description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                    <input
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                        required
                    />
                    <input
                        type="url"
                        placeholder="Submission Link"
                        value={formData.submissionLink}
                        onChange={(e) => setFormData({ ...formData, submissionLink: e.target.value })}
                    />
                    <div className={styles.formActions}>
                        <button type="submit" className={styles.saveBtn}>Save</button>
                        <button type="button" className={styles.cancelBtn} onClick={() => setShowAddForm(false)}>Cancel</button>
                    </div>
                </form>
            )}

            {/* ✅ Edit Homework Form */}
            {showEditForm && (
                <form onSubmit={handleUpdate} className={styles.modalForm}>
                    <h3>Edit Homework</h3>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                    />
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                    <input
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                        required
                    />
                    <input
                        type="url"
                        value={formData.submissionLink}
                        onChange={(e) => setFormData({ ...formData, submissionLink: e.target.value })}
                    />
                    <div className={styles.formActions}>
                        <button type="submit" className={styles.saveBtn}>Update</button>
                        <button type="button" className={styles.cancelBtn} onClick={() => setShowEditForm(false)}>Cancel</button>
                    </div>
                </form>
            )}

            <div className={styles.tableWrapper}>
                <table className={styles.homeworkTable}>
                    <thead>
                    <tr>
                        <th onClick={() => requestSort('title')}>
                            Title {sortConfig.key === 'title' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th>Description</th>
                        <th onClick={() => requestSort('deadline')}>
                            Deadline {sortConfig.key === 'deadline' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th>Submission Link</th>
                        {isCR && <th>Actions</th>}
                    </tr>
                    </thead>
                    <tbody>
                    {sortedHomeworks.map(hw => (
                        <tr key={hw.id}>
                            <td>{hw.title}</td>
                            <td>{hw.description}</td>
                            <td className={new Date(hw.deadline) < new Date() ? styles.overdue : ''}>
                                {formatDeadline(hw.deadline)}
                            </td>
                            <td>
                                {hw.submissionLink ? (
                                    <a href={hw.submissionLink} target="_blank" rel="noreferrer" className={styles.submissionLink}>
                                        Submit
                                    </a>
                                ) : '—'}
                            </td>
                            {isCR && (
                                <td>
                                    <button onClick={() => handleEdit(hw)} className={styles.editBtn}>Edit</button>
                                    <button onClick={() => handleDelete(hw.id)} className={styles.deleteBtn}>Delete</button>
                                </td>
                            )}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default StudentHomework;
